import type { LogicConditionNode, LogicContainerNode, LogicGroupNode, LogicNode, LogicSequenceNode, TradeLogicNodeId } from './types';

export type LogicNodeIdFactory = (sourceNode: LogicNode, path: number[]) => TradeLogicNodeId;

export interface LogicTreeChange<TMeta = undefined> {
    rootNode: LogicNode | null;
    changed: boolean;
    meta: TMeta;
}

export interface LogicNodeLocation {
    node: LogicNode;
    path: number[];
    parent: LogicContainerNode | null;
}

export interface AddLogicNodeOptions {
    parentId?: TradeLogicNodeId | null;
    index?: number;
}

export interface MoveLogicNodeOptions {
    targetParentId?: TradeLogicNodeId | null;
    index?: number;
}

export interface DuplicateLogicNodeOptions extends MoveLogicNodeOptions {
    idFactory: LogicNodeIdFactory;
}

export interface WrapLogicNodeOptions {
    wrapper: LogicContainerNode;
}

export interface AddLogicNodeMeta {
    addedNode: LogicNode;
    parentId: TradeLogicNodeId | null;
    index: number;
}

export interface RemoveLogicNodeMeta {
    removedNode: LogicNode;
    parentId: TradeLogicNodeId | null;
    index: number | null;
}

export interface MoveLogicNodeMeta {
    movedNode: LogicNode;
    fromParentId: TradeLogicNodeId | null;
    toParentId: TradeLogicNodeId | null;
    fromIndex: number | null;
    toIndex: number;
}

export interface DuplicateLogicNodeMeta {
    duplicatedNode: LogicNode;
    sourceNode: LogicNode;
    parentId: TradeLogicNodeId | null;
    index: number;
}

export interface WrapLogicNodeMeta {
    wrappedNode: LogicNode;
    wrapperNode: LogicContainerNode;
    parentId: TradeLogicNodeId | null;
    index: number | null;
}

export interface UnwrapLogicNodeMeta {
    unwrappedNode: LogicContainerNode;
    promotedNodes: LogicNode[];
    parentId: TradeLogicNodeId | null;
    index: number | null;
}

export const isLogicContainerNode = (node: LogicNode): node is LogicContainerNode => node.kind === 'group' || node.kind === 'sequence';

export const hasImportedConditionExpression = (node: LogicConditionNode): boolean => {
    const expression = node.metadata?.importedConditionExpression ?? node.parameters?.importedConditionExpression;

    return typeof expression === 'object' && expression !== null && !Array.isArray(expression) && typeof expression.kind === 'string';
};

export const isImportedRawConditionNode = (node: LogicNode | null): node is LogicConditionNode =>
    node?.kind === 'condition' && !node.templateKey && hasImportedConditionExpression(node);

export const normalizeImportedRawConditionNodes = (rootNode: LogicNode | null): LogicNode | null => {
    if (!rootNode) {
        return null;
    }

    return normalizeImportedRawConditionNode(rootNode);
};

const normalizeImportedRawConditionNode = (node: LogicNode): LogicNode => {
    const normalizedNode =
        isImportedRawConditionNode(node) && (node.operator !== null || node.value !== null || node.featureRef !== null)
            ? {
                  ...node,
                  featureRef: null,
                  operator: null,
                  value: null
              }
            : node;

    if (!isLogicContainerNode(normalizedNode)) {
        return normalizedNode;
    }

    const nextChildren = normalizedNode.children.map(normalizeImportedRawConditionNode);
    const childrenChanged = nextChildren.some((child, index) => child !== normalizedNode.children[index]);

    if (!childrenChanged && normalizedNode === node) {
        return node;
    }

    return {
        ...normalizedNode,
        children: nextChildren
    };
};

export const findLogicNodePath = (rootNode: LogicNode | null, nodeId: TradeLogicNodeId): number[] | null => {
    if (!rootNode) {
        return null;
    }

    const visit = (node: LogicNode, path: number[]): number[] | null => {
        if (node.id === nodeId) {
            return path;
        }

        if (!isLogicContainerNode(node)) {
            return null;
        }

        for (let index = 0; index < node.children.length; index += 1) {
            const childPath = visit(node.children[index], [...path, index]);

            if (childPath) {
                return childPath;
            }
        }

        return null;
    };

    return visit(rootNode, []);
};

export const getLogicNodeAtPath = (rootNode: LogicNode | null, path: number[]): LogicNode | null => {
    if (!rootNode) {
        return null;
    }

    let current: LogicNode = rootNode;

    for (const index of path) {
        if (!isLogicContainerNode(current) || index < 0 || index >= current.children.length) {
            return null;
        }

        current = current.children[index];
    }

    return current;
};

export const getLogicNode = (rootNode: LogicNode | null, nodeId: TradeLogicNodeId): LogicNode | null => {
    const path = findLogicNodePath(rootNode, nodeId);

    return path ? getLogicNodeAtPath(rootNode, path) : null;
};

export const getLogicNodeLocation = (rootNode: LogicNode | null, nodeId: TradeLogicNodeId): LogicNodeLocation | null => {
    const path = findLogicNodePath(rootNode, nodeId);
    const node = path ? getLogicNodeAtPath(rootNode, path) : null;

    if (!path || !node) {
        return null;
    }

    const parentPath = path.slice(0, -1);
    const parent = path.length > 0 ? getLogicNodeAtPath(rootNode, parentPath) : null;

    return {
        node,
        path,
        parent: parent && isLogicContainerNode(parent) ? parent : null
    };
};

export const flattenLogicNodes = (rootNode: LogicNode | null): LogicNodeLocation[] => {
    if (!rootNode) {
        return [];
    }

    const nodes: LogicNodeLocation[] = [];

    const visit = (node: LogicNode, path: number[], parent: LogicContainerNode | null) => {
        nodes.push({ node, path, parent });

        if (!isLogicContainerNode(node)) {
            return;
        }

        node.children.forEach((child, index) => {
            visit(child, [...path, index], node);
        });
    };

    visit(rootNode, [], null);

    return nodes;
};

export const hasLogicNode = (rootNode: LogicNode | null, nodeId: TradeLogicNodeId): boolean => findLogicNodePath(rootNode, nodeId) !== null;

export const hasDescendantLogicNode = (
    rootNode: LogicNode | null,
    ancestorId: TradeLogicNodeId,
    descendantId: TradeLogicNodeId
): boolean => {
    const ancestorPath = findLogicNodePath(rootNode, ancestorId);
    const descendantPath = findLogicNodePath(rootNode, descendantId);

    if (!ancestorPath || !descendantPath || descendantPath.length <= ancestorPath.length) {
        return false;
    }

    return ancestorPath.every((index, pathIndex) => descendantPath[pathIndex] === index);
};

export const addLogicNode = (
    rootNode: LogicNode | null,
    node: LogicNode,
    options: AddLogicNodeOptions = {}
): LogicTreeChange<AddLogicNodeMeta | null> => {
    if (!rootNode) {
        return {
            rootNode: node,
            changed: true,
            meta: {
                addedNode: node,
                parentId: null,
                index: 0
            }
        };
    }

    const parentId = options.parentId ?? null;

    if (parentId === null) {
        return {
            rootNode,
            changed: false,
            meta: null
        };
    }

    const parentPath = findLogicNodePath(rootNode, parentId);
    const parent = parentPath ? getLogicNodeAtPath(rootNode, parentPath) : null;

    if (!parentPath || !parent || !isLogicContainerNode(parent)) {
        return {
            rootNode,
            changed: false,
            meta: null
        };
    }

    const index = clampInsertIndex(options.index, parent.children.length);

    const nextParent = withContainerChildren(parent, [...parent.children.slice(0, index), node, ...parent.children.slice(index)]);

    return {
        rootNode: replaceLogicNodeAtPath(rootNode, parentPath, nextParent),
        changed: true,
        meta: {
            addedNode: node,
            parentId,
            index
        }
    };
};

export const updateLogicNode = (
    rootNode: LogicNode | null,
    nodeId: TradeLogicNodeId,
    updater: (node: LogicNode) => LogicNode
): LogicTreeChange<LogicNode | null> => {
    const path = findLogicNodePath(rootNode, nodeId);
    const node = path ? getLogicNodeAtPath(rootNode, path) : null;

    if (!rootNode || !path || !node) {
        return {
            rootNode,
            changed: false,
            meta: null
        };
    }

    const nextNode = updater(node);

    return {
        rootNode: replaceLogicNodeAtPath(rootNode, path, nextNode),
        changed: true,
        meta: nextNode
    };
};

export const removeLogicNode = (rootNode: LogicNode | null, nodeId: TradeLogicNodeId): LogicTreeChange<RemoveLogicNodeMeta | null> => {
    const path = findLogicNodePath(rootNode, nodeId);
    const removedNode = path ? getLogicNodeAtPath(rootNode, path) : null;

    if (!rootNode || !path || !removedNode) {
        return {
            rootNode,
            changed: false,
            meta: null
        };
    }

    if (path.length === 0) {
        return {
            rootNode: null,
            changed: true,
            meta: {
                removedNode,
                parentId: null,
                index: null
            }
        };
    }

    const parentPath = path.slice(0, -1);
    const index = path[path.length - 1];
    const parent = getLogicNodeAtPath(rootNode, parentPath);

    if (!parent || !isLogicContainerNode(parent)) {
        return {
            rootNode,
            changed: false,
            meta: null
        };
    }

    const nextParent = withContainerChildren(
        parent,
        parent.children.filter((_, childIndex) => childIndex !== index)
    );

    return {
        rootNode: replaceLogicNodeAtPath(rootNode, parentPath, nextParent),
        changed: true,
        meta: {
            removedNode,
            parentId: parent.id,
            index
        }
    };
};

export const moveLogicNode = (
    rootNode: LogicNode | null,
    nodeId: TradeLogicNodeId,
    options: MoveLogicNodeOptions
): LogicTreeChange<MoveLogicNodeMeta | null> => {
    const source = getLogicNodeLocation(rootNode, nodeId);
    const targetParentId = options.targetParentId ?? null;

    if (!rootNode || !source || source.path.length === 0) {
        return {
            rootNode,
            changed: false,
            meta: null
        };
    }

    if (targetParentId === nodeId || (targetParentId !== null && hasDescendantLogicNode(rootNode, nodeId, targetParentId))) {
        return {
            rootNode,
            changed: false,
            meta: null
        };
    }

    const removed = removeLogicNode(rootNode, nodeId);
    const removedRoot = removed.rootNode;
    const targetParentPath = targetParentId === null ? null : findLogicNodePath(removedRoot, targetParentId);
    const targetParent = targetParentPath ? getLogicNodeAtPath(removedRoot, targetParentPath) : null;

    if (!removed.changed || !removed.meta || !removedRoot || !targetParentPath || !targetParent || !isLogicContainerNode(targetParent)) {
        return {
            rootNode,
            changed: false,
            meta: null
        };
    }

    const insertIndex = clampInsertIndex(options.index, targetParent.children.length);

    const nextParent = withContainerChildren(targetParent, [
        ...targetParent.children.slice(0, insertIndex),
        source.node,
        ...targetParent.children.slice(insertIndex)
    ]);

    return {
        rootNode: replaceLogicNodeAtPath(removedRoot, targetParentPath, nextParent),
        changed: true,
        meta: {
            movedNode: source.node,
            fromParentId: removed.meta.parentId,
            toParentId: targetParent.id,
            fromIndex: removed.meta.index,
            toIndex: insertIndex
        }
    };
};

export const duplicateLogicNode = (
    rootNode: LogicNode | null,
    nodeId: TradeLogicNodeId,
    options: DuplicateLogicNodeOptions
): LogicTreeChange<DuplicateLogicNodeMeta | null> => {
    const source = getLogicNodeLocation(rootNode, nodeId);

    if (!rootNode || !source) {
        return {
            rootNode,
            changed: false,
            meta: null
        };
    }

    const duplicatedNode = cloneLogicNodeWithNewIds(source.node, options.idFactory, source.path);
    const parentId = options.targetParentId ?? source.parent?.id ?? null;
    const index = options.index ?? (source.path.length === 0 ? 0 : source.path[source.path.length - 1] + 1);
    const added = addLogicNode(rootNode, duplicatedNode, { parentId, index });

    if (!added.changed || !added.meta) {
        return {
            rootNode,
            changed: false,
            meta: null
        };
    }

    return {
        rootNode: added.rootNode,
        changed: true,
        meta: {
            duplicatedNode,
            sourceNode: source.node,
            parentId: added.meta.parentId,
            index: added.meta.index
        }
    };
};

export const wrapLogicNode = (
    rootNode: LogicNode | null,
    nodeId: TradeLogicNodeId,
    options: WrapLogicNodeOptions
): LogicTreeChange<WrapLogicNodeMeta | null> => {
    const location = getLogicNodeLocation(rootNode, nodeId);

    if (!rootNode || !location || hasLogicNode(location.node, options.wrapper.id)) {
        return {
            rootNode,
            changed: false,
            meta: null
        };
    }

    const wrapperNode = withContainerChildren(options.wrapper, [location.node]);
    const nextRoot = replaceLogicNodeAtPath(rootNode, location.path, wrapperNode);

    return {
        rootNode: nextRoot,
        changed: true,
        meta: {
            wrappedNode: location.node,
            wrapperNode,
            parentId: location.parent?.id ?? null,
            index: location.path.length > 0 ? location.path[location.path.length - 1] : null
        }
    };
};

export const unwrapLogicNode = (rootNode: LogicNode | null, nodeId: TradeLogicNodeId): LogicTreeChange<UnwrapLogicNodeMeta | null> => {
    const location = getLogicNodeLocation(rootNode, nodeId);

    if (!rootNode || !location || !isLogicContainerNode(location.node)) {
        return {
            rootNode,
            changed: false,
            meta: null
        };
    }

    if (location.path.length === 0) {
        if (location.node.children.length !== 1) {
            return {
                rootNode,
                changed: false,
                meta: null
            };
        }

        return {
            rootNode: location.node.children[0],
            changed: true,
            meta: {
                unwrappedNode: location.node,
                promotedNodes: [...location.node.children],
                parentId: null,
                index: null
            }
        };
    }

    const parentPath = location.path.slice(0, -1);
    const index = location.path[location.path.length - 1];
    const parent = getLogicNodeAtPath(rootNode, parentPath);

    if (!parent || !isLogicContainerNode(parent)) {
        return {
            rootNode,
            changed: false,
            meta: null
        };
    }

    const nextParent = withContainerChildren(parent, [
        ...parent.children.slice(0, index),
        ...location.node.children,
        ...parent.children.slice(index + 1)
    ]);

    return {
        rootNode: replaceLogicNodeAtPath(rootNode, parentPath, nextParent),
        changed: true,
        meta: {
            unwrappedNode: location.node,
            promotedNodes: [...location.node.children],
            parentId: parent.id,
            index
        }
    };
};

const clampInsertIndex = (index: number | undefined, length: number): number => {
    if (index === undefined || Number.isNaN(index)) {
        return length;
    }

    return Math.min(Math.max(0, Math.trunc(index)), length);
};

const withContainerChildren = (node: LogicContainerNode, children: LogicNode[]): LogicContainerNode => {
    if (node.kind === 'group') {
        const nextNode: LogicGroupNode = {
            ...node,
            children
        };

        return nextNode;
    }

    const nextNode: LogicSequenceNode = {
        ...node,
        children
    };

    return nextNode;
};

const replaceLogicNodeAtPath = (rootNode: LogicNode, path: number[], replacement: LogicNode): LogicNode => {
    if (path.length === 0) {
        return replacement;
    }

    const [index, ...childPath] = path;

    if (!isLogicContainerNode(rootNode)) {
        return rootNode;
    }

    const children = rootNode.children.map((child, childIndex) =>
        childIndex === index ? replaceLogicNodeAtPath(child, childPath, replacement) : child
    );

    return withContainerChildren(rootNode, children);
};

const cloneLogicNodeWithNewIds = (node: LogicNode, idFactory: LogicNodeIdFactory, path: number[]): LogicNode => {
    const nextId = idFactory(node, path);

    if (!isLogicContainerNode(node)) {
        return {
            ...node,
            id: nextId
        };
    }

    const children = node.children.map((child, index) => cloneLogicNodeWithNewIds(child, idFactory, [...path, index]));
    const cloned =
        node.kind === 'sequence'
            ? {
                  ...node,
                  stepTiming: Object.fromEntries(
                      node.children.flatMap((child, index) => {
                          const timing = node.stepTiming[child.id];
                          return timing ? [[children[index].id, timing]] : [];
                      })
                  ),
                  stepCaptures: Object.fromEntries(
                      node.children.flatMap((child, index) => {
                          const capture = node.stepCaptures?.[child.id];
                          return capture ? [[children[index].id, capture]] : [];
                      })
                  )
              }
            : node;
    return withContainerChildren(
        {
            ...cloned,
            id: nextId
        },
        children
    );
};
